import {
  PaletteOptions,
  alpha,
  createTheme,
  darken,
} from '@mui/material/styles';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { svSE } from '@mui/material/locale';
import { svSE as datagridLocale } from '@mui/x-data-grid-pro/locales';
import {
  accordionClasses,
  // AutocompleteClassKey,
  buttonClasses,
  filledInputClasses,
  iconButtonClasses,
  inputLabelClasses,
  outlinedInputClasses,
  Breakpoint as MuiBreakpoint,
} from '@mui/material';
import { themeColors } from './AvenyThemeColors';
interface IColor {
  full: string;
  faded: string;
}

//Vi använder standardbreakpoints från Mui i AvenyTheme
export type Breakpoint = MuiBreakpoint;

export const APP_NAVBAR_HEIGHT_PX = 48;
export const BREADCRUMB_BAR_HEIGHT_PX = 40;
export const APP_HEADER_TOTAL_HEIGHT_PX =
  APP_NAVBAR_HEIGHT_PX + BREADCRUMB_BAR_HEIGHT_PX;

// Add custom properties to the Theme type
declare module '@mui/material/styles' {
  //eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    customProperties: {
      specialText: string;
      burial: {
        buttonBackground: string;
        buttonBorder: string;
        buttonBorderHover: string;
      };
      extralightPrimary?: string;
      selectFocusBackgroundColor: string;
      highlightedTextColor: string;
      backgroundImageColor: string;
      businessDataFormEditMenuHeight: number;
      businessCollectionMenuSmallHeight: number;
      stepperBarHeight: number;
      panelHeadBg: {
        dark: string;
        mediumDark: string;
        mediumLight: string;
        light: string;
      };
      Dialog: {
        headerBg: string;
        headerColor: string;
        actionsBg: string;
        actionsColor: string;
        actionsBorder: string;
        actionDivider: string;
      };
      DataGrid: {
        activeCellBackgroundColor: string;
        rowHoverBackgroundColor: string;
        activeRowBackgroundColor: string;
        gridBorderColor: string;
        lightGridBorderColor: string;
      };
      errorComponent: {
        accordionError: string;
        timestampError: string;
      };
      Tabs: {
        IndicatorColor: {
          light: string;
          dark: string;
        };
        divider: string;
      };
      MenuList: {
        baseBg: string;
        activeBg: string;
        activeListBg: string;
        hover: string;
        listBorder: string;
        inactiveBorder: string;
      };
      MessageList: {
        unreadBackground: string;
        unreadBackgroundActive: string;
        unreadIcon: string;
        unreadIconActive: string;
        readBackground: string;
        readIcon: string;
        readIconActive: string;
        unreadMessageTextHi: string;
        unreadMessageTextLow: string;
        readMessageTextHi: string;
        readMessageTextLow: string;
      };
      Drag: {
        border: string;
        background: string;
      };
      Warning: {
        ultraUltraLight: string;
        ultralight: string;
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      ProcessColors: {
        processHeaderBg: string;
        processMediumHeaderBg: string;
        processLightHeaderBg: string;
        activeStepBg: string;
        passiveStepBg: string;
        processButtonColor: string;
        processButtonActiveColor: string;
        processButtonActiveBorder: string;
      };
      ActivityCardPanel: {
        rootLightBg: string;
        rootMediumBg: string;
        border: string;
      };
      AccountCard: {
        topValueTextColor: string;
        lowerValueFontWeight: any;
      };
      NeutralColors: {
        lightGreyMiniText: string;
        mediumGreyForIconButtons: string;
      };
      AutocompleteColors: {
        searchText: string;
        searchTextHighlight: string;
        background: string;
        underline: string;
        label: string;
      };
      VyValjareColors: {
        tabs: string;
      };
      EkonomiskAnalysRowColors: {
        selectedRowBoxBGColor: string;
        selectedRowBGColor: string;
        selectedRowFGColor: string;
        selectedColumnBGColor: string;
        lightBg: string;
        mediumBg: string;
        parentBg: string;
      };
      EkonomiskAnalysDetailTableColors: {
        border: string;
      };
      EASideMenuColors: {
        selectedOptionsSectionBGColor: string;
        itemListBottomBorder: string;
        menuBGColor: string;
        selectedSectionBGColor: string;
        sectionHeaderFGColor: string;
        analysFGColor: string;
      };
      EkonomiskAnalysTableBorderColor: string;
      AvatarList: {
        inactiveBackground: string;
        inactiveTextColor: string;
        inactiveBorder: string;
        inactiveHoverBackground: string;
        inactiveHoverTextColor: string;
        inactiveHoverBorder: string;
        activeBackground: string;
        activeTextColor: string;
        activeBorder: string;
        activeHoverBackground: string;
        activeHoverTextColor: string;
        activeHoverBorder: string;
      };
      FormLabel: {
        border: string;
      };
      Fade: {
        LightOnDark: {
          highEmphasis: string;
          mediumEmphasis: string;
          lowEmphasis: string;
          divider: string;
        };
        DarkOnLight: {
          highEmphasis: string;
          mediumEmphasis: string;
          lowEmphasis: string;
          divider: string;
        };
        BlueGray: {
          mediumEmphasis: string;
          lowEmphasis: string;
          minimalEmphasis: string;
          hover: string;
          selectedLightFade: string;
          selected: string;
        };
        Teal: {
          mediumEmphasis: string;
          lowEmphasis: string;
          lightEmphasis: string;
        };
      };
      Shadows: {
        bottomShadow: string;
        topShadow: string;
      };
      Legend: {
        lightBlue: IColor;
        lightGreen: IColor;
        indigo: IColor;
        gray: IColor;
        red: IColor;
        darkGreen: IColor;
        darkBlue: IColor;
        teal: IColor;
        pink: IColor;
        yellow: IColor;
        orange: IColor;
        blueGray: IColor;
        begrColor: IColor;
        dopColor: IColor;
        vigselColor: IColor;
        konfColor: IColor;
        ovrigColor: IColor;
        gudstColor: IColor;
        lokalColor: IColor;
        gravsColor: IColor;
        snabbColor: IColor;
        outlookColor: IColor;
      };
      List: {
        statusBackground: string;
        statusColor: string;
      };
      AktivitetColors: {
        gudstjanst: string;
        begravning: string;
        dop: string;
        vigsel: string;
        konfirmation: string;
        aktovrigt: string;
        gravsattning: string;
        snabbokning: string;
        lokaluthyrning: string;
        outlook: string;
        dayName: string;
        light: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
        };
        stalltid: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          bokningsbar: {
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            lokaluthyrning: string;
          };
        };
        bokningsbar: {
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          lokaluthyrning: string;
        };
        preliminar: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          light: {
            gudstjanst: string;
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            snabbokning: string;
            lokaluthyrning: string;
            outlook: string;
          };
        };
        preliminarNyVy: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          light: {
            gudstjanst: string;
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            snabbokning: string;
            lokaluthyrning: string;
            outlook: string;
          };
        };
      };
      softRainbow: {
        sage: string;
        terracotta: string;
        dustyPlum: string;
        powderBlue: string;
        apricot: string;
        eucalyptus: string;
        burntSienna: string;
        camel: string;
        pineMist: string;
        greige: string;
        stormBlue: string;
        oliveGold: string;
        mutedLilac: string;
        roseClay: string;
        fjord: string;
        khaki: string;
        linen: string;
        pearlGray: string;
        iris: string;
      };
      customPlanering: {
        lightBlue: string;
        fullBlue: string;
      };
      customPlaneringAktivitet: {
        lightGreen: string;
        fullGreen: string;
      };
      customPlaneringPast: {
        lightGrey: string;
      };
      dustyRainbow: {
        glauca: string;
        salmon: string;
        chambray: string;
        amber: string;
        heather: string;
        pistachio: string;
        redwood: string;
        oat: string;
        conifer: string;
        pebble: string;
        twilight: string;
        artichoke: string;
        hyacinth: string;
        blush: string;
        lagoon: string;
        walnut: string;
      };
      customDetaljanalys: {
        parchment: string;
        canvas: string;
        ash: string;
        jade: string;
        juniper: string;
        garnet: string;
        sapphire: string;
        deepBlue: string;
      };
    };
  }
  // allow configuration using `createMuiTheme`
  //eslint-disable-next-line @typescript-eslint/naming-convention
  interface ThemeOptions {
    customProperties?: {
      specialText?: string;
      extralightPrimary?: string;
      burial?: {
        buttonBackground: string;
        buttonBorder: string;
        buttonBorderHover: string;
      };
      selectFocusBackgroundColor?: string;
      highlightedTextColor?: string;
      backgroundImageColor?: string;
      businessDataFormEditMenuHeight?: number;
      businessCollectionMenuSmallHeight?: number;
      stepperBarHeight?: number;
      panelHeadBg?: {
        dark: string;
        mediumDark: string;
        mediumLight: string;
        light: string;
      };
      Dialog?: {
        headerBg: string;
        headerColor: string;
        actionsBg: string;
        actionsColor: string;
        actionsBorder: string;
        actionDivider: string;
      };
      DataGrid?: {
        activeCellBackgroundColor: string;
        rowHoverBackgroundColor: string;
        activeRowBackgroundColor: string;
        gridBorderColor: string;
        lightGridBorderColor: string;
      };
      errorComponent?: {
        accordionError: string;
        timestampError: string;
      };
      Tasks?: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      Tabs?: {
        IndicatorColor: {
          light: string;
          dark: string;
        };
        divider: string;
      };
      MenuList?: {
        baseBg: string;
        activeBg: string;
        activeListBg: string;
        hover: string;
        listBorder: string;
        inactiveBorder: string;
      };
      MessageList?: {
        unreadBackground: string;
        unreadBackgroundActive: string;
        unreadIcon: string;
        unreadIconActive: string;
        readBackground: string;
        readIcon: string;
        readIconActive: string;
        unreadMessageTextHi: string;
        unreadMessageTextLow: string;
        readMessageTextHi: string;
        readMessageTextLow: string;
      };
      Drag?: {
        border: string;
        background: string;
      };
      Warning?: {
        ultraUltraLight: string;
        ultralight: string;
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      ProcessColors?: {
        processHeaderBg: string;
        processMediumHeaderBg: string;
        processLightHeaderBg: string;
        activeStepBg: string;
        passiveStepBg: string;
        processButtonColor: string;
        processButtonActiveColor: string;
        processButtonActiveBorder: string;
      };
      ActivityCardPanel?: {
        rootLightBg: string;
        rootMediumBg: string;
        border: string;
      };
      AccountCard?: {
        topValueTextColor: string;
        lowerValueFontWeight: any; // är det här verkligen nödvändigt?
      };
      NeutralColors?: {
        lightGreyMiniText: string;
        mediumGreyForIconButtons: string;
      };
      AutocompleteColors?: {
        searchText: string;
        searchTextHighlight: string;
        background: string;
        underline: string;
        label: string;
      };

      VyValjareColors?: {
        tabs: string;
      };
      EkonomiskAnalysRowColors?: {
        selectedRowBoxBGColor: string;
        selectedRowBGColor: string;
        selectedRowFGColor: string;
        selectedColumnBGColor: string;
        lightBg: string;
        mediumBg: string;
        parentBg: string;
      };
      EkonomiskAnalysTableBorderColor?: string;
      EkonomiskAnalysDetailTableColors?: {
        border: string;
      };
      EASideMenuColors?: {
        selectedOptionsSectionBGColor: string;
        itemListBottomBorder: string;
        menuBGColor: string;
        selectedSectionBGColor: string;
        sectionHeaderFGColor: string;
        analysFGColor: string;
      };
      AvatarList?: {
        inactiveBackground: string;
        inactiveTextColor: string;
        inactiveBorder: string;
        inactiveHoverBackground: string;
        inactiveHoverTextColor: string;
        inactiveHoverBorder: string;
        activeBackground: string;
        activeTextColor: string;
        activeBorder: string;
        activeHoverBackground: string;
        activeHoverTextColor: string;
        activeHoverBorder: string;
      };
      FormLabel?: {
        border: string;
      };
      Fade?: {
        LightOnDark: {
          highEmphasis: string;
          mediumEmphasis: string;
          lowEmphasis: string;
          divider: string;
        };
        DarkOnLight: {
          highEmphasis: string;
          mediumEmphasis: string;
          lowEmphasis: string;
          divider: string;
        };
        BlueGray: {
          mediumEmphasis: string;
          lowEmphasis: string;
          minimalEmphasis: string;
          hover: string;
          selectedLightFade: string;
          selected: string;
        };
        Teal: {
          mediumEmphasis: string;
          lowEmphasis: string;
          lightEmphasis: string;
        };
      };
      Shadows?: {
        bottomShadow: string;
        topShadow: string;
      };
      Legend?: {
        lightBlue: IColor;
        lightGreen: IColor;
        indigo: IColor;
        gray: IColor;
        red: IColor;
        darkGreen: IColor;
        darkBlue: IColor;
        teal: IColor;
        pink: IColor;
        yellow: IColor;
        orange: IColor;
        blueGray: IColor;
        begrColor: IColor;
        dopColor: IColor;
        vigselColor: IColor;
        konfColor: IColor;
        ovrigColor: IColor;
        gudstColor: IColor;
        lokalColor: IColor;
        gravsColor: IColor;
        snabbColor: IColor;
        outlookColor: IColor;
      };
      Delsystem?: {
        bokningBrandColor: string;
        bokningBrandColorDark: string;
      };
      List?: {
        statusBackground: string;
        statusColor: string;
      };
      AktivitetColors?: {
        gudstjanst: string;
        begravning: string;
        dop: string;
        vigsel: string;
        konfirmation: string;
        aktovrigt: string;
        gravsattning: string;
        snabbokning: string;
        lokaluthyrning: string;
        outlook: string;
        dayName: string;
        light?: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
        };
        stalltid?: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          bokningsbar?: {
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            lokaluthyrning: string;
          };
        };
        bokningsbar?: {
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          lokaluthyrning: string;
        };
        preliminar?: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          light?: {
            gudstjanst: string;
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            snabbokning: string;
            lokaluthyrning: string;
            outlook: string;
          };
        };
        preliminarNyVy?: {
          gudstjanst: string;
          begravning: string;
          dop: string;
          vigsel: string;
          konfirmation: string;
          aktovrigt: string;
          gravsattning: string;
          snabbokning: string;
          lokaluthyrning: string;
          outlook: string;
          light?: {
            gudstjanst: string;
            begravning: string;
            dop: string;
            vigsel: string;
            konfirmation: string;
            aktovrigt: string;
            gravsattning: string;
            snabbokning: string;
            lokaluthyrning: string;
            outlook: string;
          };
        };
      };
      softRainbow?: {
        sage: string;
        terracotta: string;
        dustyPlum: string;
        powderBlue: string;
        apricot: string;
        eucalyptus: string;
        burntSienna: string;
        camel: string;
        pineMist: string;
        greige: string;
        stormBlue: string;
        oliveGold: string;
        mutedLilac: string;
        roseClay: string;
        fjord: string;
        khaki: string;
        linen: string;
        pearlGray: string;
        iris: string;
      };
      customPlanering: {
        lightBlue: string;
        fullBlue: string;
      };
      customPlaneringAktivitet: {
        lightGreen: string;
        fullGreen: string;
      };
      customPlaneringPast: {
        lightGrey: string;
      };
      dustyRainbow?: {
        glauca: string;
        salmon: string;
        chambray: string;
        amber: string;
        heather: string;
        pistachio: string;
        redwood: string;
        oat: string;
        conifer: string;
        pebble: string;
        twilight: string;
        artichoke: string;
        hyacinth: string;
        blush: string;
        lagoon: string;
        walnut: string;
      };
      customDetaljanalys?: {
        parchment: string;
        canvas: string;
        ash: string;
        jade: string;
        juniper: string;
        garnet: string;
        sapphire: string;
        deepBlue: string;
      };
    };
  }
}

/**
 * Colors that are used in multiple places in theme but should always be the same
 */
export const lightThemeTextPrimaryColor = alpha('#000', 0.87);
/**
 * Start with an empty "theme", that in effect imports the standard theme.
 */
const theme = createTheme();

const BaseTheme = createTheme(
  {
    typography: {
      body1: {
        fontSize: '0.875rem',
      },
      overline: {
        lineHeight: '1.5',
      },
      caption: {
        '&$paragraph': {
          marginBottom: 'auto',
          lineHeight: 1.3,
        },
      },
    },
    customProperties: {
      specialText: themeColors.orange[800],
      extralightPrimary: themeColors.teal.A300,
      burial: {
        buttonBackground: themeColors.teal[100],
        buttonBorder: themeColors.teal.A400,
        buttonBorderHover: themeColors.teal[500],
      },
      selectFocusBackgroundColor: themeColors.blueGrayFade[200.87],
      highlightedTextColor: themeColors.blackFade[1000.87],
      backgroundImageColor: themeColors.teal[200],
      businessDataFormEditMenuHeight: 55,
      businessCollectionMenuSmallHeight: 52,
      stepperBarHeight: 32,
      panelHeadBg: {
        dark: themeColors.blueGray[800],
        mediumDark: themeColors.blueGray[700],
        mediumLight: themeColors.blueGray[650],
        light: themeColors.blueGray[600],
      },
      Dialog: {
        headerBg: themeColors.blueGray[700],
        headerColor: themeColors.whiteFade[1000.74],
        actionsBg: themeColors.gray[200],
        actionsBorder: themeColors.gray[300],
        actionsColor: themeColors.blackFade[1000.6],
        actionDivider: themeColors.blackFade[1000.12],
      },
      DataGrid: {
        activeCellBackgroundColor: themeColors.white[1000],
        rowHoverBackgroundColor: themeColors.grayFade[100.65],
        activeRowBackgroundColor: themeColors.grayFade[50.8],
        gridBorderColor: themeColors.gray[200],
        lightGridBorderColor: themeColors.gray[150],
      },
      errorComponent: {
        accordionError: themeColors.tealFade[100.2],
        timestampError: themeColors.teal[300],
      },
      Tasks: {
        light: themeColors.lightBlue[500],
        main: themeColors.lightBlue[600],
        dark: themeColors.lightBlue[800],
        contrastText: themeColors.white[1000],
      },
      Tabs: {
        IndicatorColor: {
          light: themeColors.white[1000],
          dark: themeColors.blueGray[800],
        },
        divider: 'inset 0 -1px rgb(0 0 0 / 0.24)',
      },
      MenuList: {
        baseBg: themeColors.blueGray[50],
        activeBg: themeColors.gray[200],
        activeListBg: themeColors.gray[100],
        hover: themeColors.blueGray[200],
        listBorder: '1px solid' + themeColors.gray[300],
        inactiveBorder: '1px solid transparent',
      },
      MessageList: {
        unreadBackground: themeColors.blue[800],
        unreadBackgroundActive: themeColors.blue[900],
        unreadIcon: themeColors.whiteFade[1000.38],
        unreadIconActive: themeColors.whiteFade[1000.96],
        readBackground: themeColors.blueGray[100],
        readIcon: themeColors.blueGrayFade[700.6],
        readIconActive: themeColors.blueGrayFade[700.6],
        unreadMessageTextHi: themeColors.white[1000],
        unreadMessageTextLow: themeColors.whiteFade[1000.96],
        readMessageTextHi: themeColors.blackFade[1000.87],
        readMessageTextLow: themeColors.blackFade[1000.6],
      },
      Drag: {
        border: '1px solid ' + themeColors.blueGray[200],
        background: themeColors.blueGray[100],
      },
      Warning: {
        ultraUltraLight: themeColors.orange[50],
        ultralight: themeColors.orange[200],
        light: themeColors.orange[500],
        main: themeColors.orange[700],
        dark: themeColors.orange.A800,
        contrastText: themeColors.white[1000],
      },
      ProcessColors: {
        processHeaderBg: themeColors.blueGray[400],
        processMediumHeaderBg: themeColors.blueGray[400],
        processLightHeaderBg: themeColors.blueGray[200],
        activeStepBg: themeColors.amber[650],
        passiveStepBg: themeColors.blueGray[200],
        processButtonColor: themeColors.blueGray[400],
        processButtonActiveColor: themeColors.teal[500],
        processButtonActiveBorder: themeColors.blueGray[500],
      },
      ActivityCardPanel: {
        rootLightBg: themeColors.blueGray[100],
        rootMediumBg: themeColors.blueGray[200],
        border: '1px solid ' + themeColors.blueGray[200],
      },
      AccountCard: {
        topValueTextColor: themeColors.blue[600],
        lowerValueFontWeight: 500,
      },
      NeutralColors: {
        lightGreyMiniText: themeColors.gray[400],
        mediumGreyForIconButtons: themeColors.gray[600],
      },
      AutocompleteColors: {
        searchText: themeColors.blueGray[400],
        searchTextHighlight: themeColors.blueGray[800],
        background: themeColors.blackFade[1000.09],
        underline: themeColors.blackFade[1000.42],
        label: themeColors.blackFade[1000.6],
      },
      VyValjareColors: {
        tabs: themeColors.blueGray[700],
      },
      EkonomiskAnalysRowColors: {
        selectedRowBoxBGColor: themeColors.blueGray[200],
        selectedRowBGColor: themeColors.blueGray[400],
        selectedRowFGColor: themeColors.lightGray[500],
        selectedColumnBGColor: themeColors.blueGray[700],
        lightBg: themeColors.lightGray[300],
        mediumBg: themeColors.lightGray[450],
        parentBg: darken(themeColors.blueGray[100], 0.01),
      },
      EkonomiskAnalysTableBorderColor: themeColors.gray[300],
      EkonomiskAnalysDetailTableColors: {
        border: themeColors.gray[300],
      },
      EASideMenuColors: {
        selectedOptionsSectionBGColor: themeColors.blueGray[100],
        itemListBottomBorder: '1px solid' + themeColors.gray[300],
        menuBGColor: themeColors.gray[100],
        selectedSectionBGColor: themeColors.gray[300],
        sectionHeaderFGColor: themeColors.black[1000],
        analysFGColor: themeColors.gray[700],
      },
      AvatarList: {
        inactiveBackground: themeColors.blueGray[600],
        inactiveTextColor: themeColors.whiteFade[1000.74],
        inactiveBorder: '1px',
        inactiveHoverBackground: themeColors.blueGray[800],
        inactiveHoverTextColor: themeColors.whiteFade[1000.96],
        inactiveHoverBorder: '1px',
        activeBackground: themeColors.teal[500],
        activeTextColor: themeColors.whiteFade[1000.96],
        activeBorder: '1px solid ' + themeColors.teal[500],

        activeHoverBackground: themeColors.teal[700],
        activeHoverTextColor: themeColors.whiteFade[1000.96],
        activeHoverBorder: '1px solid ' + themeColors.teal[700],
      },
      FormLabel: {
        border: '1px solid' + themeColors.gray[300],
      },
      Fade: {
        LightOnDark: {
          highEmphasis: themeColors.whiteFade[1000.96],
          mediumEmphasis: themeColors.whiteFade[1000.74],
          lowEmphasis: themeColors.whiteFade[1000.38],
          divider: themeColors.whiteFade[1000.12],
        },
        DarkOnLight: {
          highEmphasis: themeColors.blackFade[1000.87],
          mediumEmphasis: themeColors.blackFade[1000.6],
          lowEmphasis: themeColors.blackFade[1000.38],
          divider: themeColors.blackFade[1000.12],
        },
        BlueGray: {
          mediumEmphasis: themeColors.blueGrayFade[100.87],
          lowEmphasis: themeColors.blueGrayFade[100.7],
          minimalEmphasis: themeColors.blueGrayFade[100.26],
          hover: themeColors.blueGrayFade[700.12],
          selectedLightFade: themeColors.blueGrayFade[900.1],
          selected: themeColors.blueGrayFade[700.16],
        },
        Teal: {
          mediumEmphasis: themeColors.tealFade[500.6],
          lowEmphasis: themeColors.tealFade[500.38],
          lightEmphasis: themeColors.tealFade[500.15],
        },
      },
      Shadows: {
        bottomShadow: `0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 12px 8px -8px rgba(0,0,0,0.12)`,
        topShadow:
          '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 0px 7px 6px rgba(0,0,0,0.2)',
      },
      Legend: {
        gray: {
          full: themeColors.gray[600],
          faded: themeColors.grayFade[100.65],
        },
        lightBlue: {
          full: themeColors.lightBlue[700],
          faded: themeColors.lightBlueFade[700.3],
        },
        lightGreen: {
          full: themeColors.green[700],
          faded: themeColors.greenFade[700.3],
        },
        indigo: {
          full: themeColors.indigo[400],
          faded: themeColors.indigoFade[700.3],
        },
        red: {
          full: themeColors.red.A800,
          faded: themeColors.redFade[800.3],
        },
        darkGreen: {
          full: themeColors.green[900],
          faded: themeColors.greenFade[900.3],
        },
        darkBlue: {
          full: themeColors.blue[900],
          faded: themeColors.blueFade[900.3],
        },
        teal: {
          full: themeColors.teal[500],
          faded: themeColors.tealFade[500.3],
        },
        pink: {
          full: themeColors.pink[700],
          faded: themeColors.pinkFade[700.3],
        },
        orange: {
          full: themeColors.orange[800],
          faded: themeColors.orangeFade[800.3],
        },
        blueGray: {
          full: themeColors.blueGray[650],
          faded: themeColors.blueGrayFade[650.3],
        },
        yellow: {
          full: themeColors.yellow[500],
          faded: themeColors.yellowFade[500.3],
        },
        begrColor: {
          full: themeColors.custom.green,
          faded: themeColors.customFade.green,
        },
        dopColor: {
          full: themeColors.custom.yellow,
          faded: themeColors.customFade.yellow,
        },
        vigselColor: {
          full: themeColors.custom.orange,
          faded: themeColors.customFade.orange,
        },
        konfColor: {
          full: themeColors.custom.lavender,
          faded: themeColors.customFade.lavender,
        },
        ovrigColor: {
          full: themeColors.custom.dodgerBlue,
          faded: themeColors.customFade.dodgerBlue,
        },
        gudstColor: {
          full: themeColors.custom.mediumPurple,
          faded: themeColors.customFade.mediumPurple,
        },
        lokalColor: {
          full: themeColors.custom.skyBlue,
          faded: themeColors.customFade.skyBlue,
        },
        gravsColor: {
          full: themeColors.custom.tan,
          faded: themeColors.customFade.tan,
        },
        snabbColor: {
          full: themeColors.custom.salmon,
          faded: themeColors.customFade.salmon,
        },
        outlookColor: {
          full: themeColors.custom.silver,
          faded: themeColors.customFade.silver,
        },
      },
      List: {
        statusBackground: themeColors.purple[200],
        statusColor: themeColors.purple[800],
      },
      AktivitetColors: {
        gudstjanst: themeColors.custom.mediumPurple,
        begravning: themeColors.custom.green,
        dop: themeColors.custom.yellow,
        vigsel: themeColors.custom.orange,
        konfirmation: themeColors.custom.lavender,
        aktovrigt: themeColors.custom.dodgerBlue,
        gravsattning: themeColors.custom.tan,
        snabbokning: themeColors.custom.salmon,
        lokaluthyrning: themeColors.custom.skyBlue,
        outlook: themeColors.custom.silver,
        dayName: themeColors.white[1000],
        light: {
          gudstjanst: themeColors.customLight.mediumPurple,
          begravning: themeColors.customLight.green,
          dop: themeColors.customLight.yellow,
          vigsel: themeColors.customLight.orange,
          konfirmation: themeColors.customLight.lavender,
          aktovrigt: themeColors.customLight.skyBlue,
          gravsattning: themeColors.customLight.tan,
          snabbokning: themeColors.customLight.salmon,
          lokaluthyrning: themeColors.customLight.dodgerBlue,
          outlook: themeColors.customLight.silver,
        },
        stalltid: {
          gudstjanst: themeColors.customFade.mediumPurple,
          begravning: themeColors.customFade.green,
          dop: themeColors.customFade.yellow,
          vigsel: themeColors.customFade.orange,
          konfirmation: themeColors.customFade.lavender,
          aktovrigt: themeColors.customFade.dodgerBlue,
          gravsattning: themeColors.customFade.tan,
          snabbokning: themeColors.customFade.salmon,
          lokaluthyrning: themeColors.customFade.skyBlue,
          outlook: themeColors.customFade.silver,
          bokningsbar: {
            begravning: themeColors.customFade.lightGreen,
            dop: themeColors.customFade.lightYellow,
            vigsel: themeColors.customFade.lightOrange,
            konfirmation: themeColors.customFade.lightLavender,
            aktovrigt: themeColors.customFade.lightDodgerBlue,
            gravsattning: themeColors.customFade.lightTan,
            lokaluthyrning: themeColors.customFade.lightSkyBlue,
          },
        },
        bokningsbar: {
          begravning: themeColors.customFade.green,
          dop: themeColors.customFade.yellow,
          vigsel: themeColors.customFade.orange,
          konfirmation: themeColors.customFade.lavender,
          aktovrigt: themeColors.customFade.dodgerBlue,
          gravsattning: themeColors.customFade.tan,
          lokaluthyrning: themeColors.customFade.skyBlue,
        },
        preliminar: {
          gudstjanst: themeColors.customStriped.mediumPurple,
          begravning: themeColors.customStriped.green,
          dop: themeColors.customStriped.yellow,
          vigsel: themeColors.customStriped.orange,
          konfirmation: themeColors.customStriped.lavender,
          aktovrigt: themeColors.customStriped.dodgerBlue,
          gravsattning: themeColors.customStriped.tan,
          snabbokning: themeColors.customStriped.salmon,
          lokaluthyrning: themeColors.customStriped.skyBlue,
          outlook: themeColors.customStriped.silver,
          light: {
            gudstjanst: themeColors.customStripedLight.mediumPurple,
            begravning: themeColors.customStripedLight.green,
            dop: themeColors.customStripedLight.yellow,
            vigsel: themeColors.customStripedLight.orange,
            konfirmation: themeColors.customStripedLight.lavender,
            aktovrigt: themeColors.customStripedLight.dodgerBlue,
            gravsattning: themeColors.customStripedLight.tan,
            snabbokning: themeColors.customStripedLight.salmon,
            lokaluthyrning: themeColors.customStripedLight.skyBlue,
            outlook: themeColors.customStripedLight.silver,
          },
        },
        preliminarNyVy: {
          gudstjanst: themeColors.customPreliminaryNewViews.mediumPurple,
          begravning: themeColors.customPreliminaryNewViews.green,
          dop: themeColors.customPreliminaryNewViews.yellow,
          vigsel: themeColors.customPreliminaryNewViews.orange,
          konfirmation: themeColors.customPreliminaryNewViews.lavender,
          aktovrigt: themeColors.customPreliminaryNewViews.dodgerBlue,
          gravsattning: themeColors.customPreliminaryNewViews.tan,
          snabbokning: themeColors.customPreliminaryNewViews.salmon,
          lokaluthyrning: themeColors.customPreliminaryNewViews.skyBlue,
          outlook: themeColors.customPreliminaryNewViews.silver,
          light: {
            gudstjanst: themeColors.white[1000],
            begravning: themeColors.white[1000],
            dop: themeColors.white[1000],
            vigsel: themeColors.white[1000],
            konfirmation: themeColors.white[1000],
            aktovrigt: themeColors.white[1000],
            gravsattning: themeColors.white[1000],
            snabbokning: themeColors.white[1000],
            lokaluthyrning: themeColors.white[1000],
            outlook: themeColors.white[1000],
          },
        },
      },
      softRainbow: {
        sage: themeColors.softRainbow.sage,
        terracotta: themeColors.softRainbow.terracotta,
        dustyPlum: themeColors.softRainbow.dustyPlum,
        powderBlue: themeColors.softRainbow.powderBlue,
        apricot: themeColors.softRainbow.apricot,
        eucalyptus: themeColors.softRainbow.eucalyptus,
        burntSienna: themeColors.softRainbow.burntSienna,
        camel: themeColors.softRainbow.camel,
        pineMist: themeColors.softRainbow.pineMist,
        greige: themeColors.softRainbow.greige,
        stormBlue: themeColors.softRainbow.stormBlue,
        oliveGold: themeColors.softRainbow.oliveGold,
        mutedLilac: themeColors.softRainbow.mutedLilac,
        roseClay: themeColors.softRainbow.roseClay,
        fjord: themeColors.softRainbow.fjord,
        khaki: themeColors.softRainbow.khaki,
        linen: themeColors.softRainbow.linen,
        pearlGray: themeColors.softRainbow.pearlGray,
        iris: themeColors.softRainbow.iris,
      },
      customPlanering: {
        lightBlue: themeColors.customPlanering.lightBlue,
        fullBlue: themeColors.customPlanering.fullBlue,
      },
      customPlaneringAktivitet: {
        lightGreen: themeColors.customPlaneringAktivitet.lightGreen,
        fullGreen: themeColors.customPlaneringAktivitet.fullGreen,
      },
      customPlaneringPast: {
        lightGrey: themeColors.customPlaneringPast.lightGrey,
      },
      dustyRainbow: {
        glauca: themeColors.dustyRainbow.glauca,
        salmon: themeColors.dustyRainbow.salmon,
        chambray: themeColors.dustyRainbow.chambray,
        amber: themeColors.dustyRainbow.amber,
        heather: themeColors.dustyRainbow.heather,
        pistachio: themeColors.dustyRainbow.pistachio,
        redwood: themeColors.dustyRainbow.redwood,
        oat: themeColors.dustyRainbow.oat,
        conifer: themeColors.dustyRainbow.conifer,
        pebble: themeColors.dustyRainbow.pebble,
        twilight: themeColors.dustyRainbow.twilight,
        artichoke: themeColors.dustyRainbow.artichoke,
        hyacinth: themeColors.dustyRainbow.hyacinth,
        blush: themeColors.dustyRainbow.blush,
        lagoon: themeColors.dustyRainbow.lagoon,
        walnut: themeColors.dustyRainbow.walnut,
      },
      customDetaljanalys: {
        parchment: themeColors.customDetaljanalys.parchment,
        canvas: themeColors.customDetaljanalys.canvas,
        ash: themeColors.customDetaljanalys.ash,
        jade: themeColors.customDetaljanalys.jade,
        juniper: themeColors.customDetaljanalys.juniper,
        garnet: themeColors.customDetaljanalys.garnet,
        sapphire: themeColors.customDetaljanalys.sapphire,
        deepBlue: themeColors.customDetaljanalys.deepBlue,
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            [`&.${accordionClasses.expanded}`]: {
              marginBottom: theme.spacing(1),
              '&:not(:first-of-type)': {
                borderTop: `1px solid ${themeColors.blackFade[1000.12]}`,
                '& +[class*="MuiAccordion-root"]': {
                  borderTop: `1px solid ${themeColors.blackFade[1000.12]}`,
                },
              },
              '&:last-child': {
                marginBottom: theme.spacing(1),
              },
            },
            [`&.${accordionClasses.expanded} + &`]: {
              '&:before': {
                display: 'block',
                opacity: 1,
              },
            },
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          container: {
            alignContent: 'flex-start',
            maxHeight: 'fit-content',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            [`&.${filledInputClasses.disabled}`]: {
              backgroundColor: themeColors.blackFade[1000.08],
            },
            padding: theme.spacing(0, 0.5),
          },
          input: {
            fontSize: '0.875rem',
            padding: theme.spacing(2, 0, 0.5),
            height: '1.3575em',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          colorInherit: {
            color: 'inherit',
            [`&.${iconButtonClasses.disabled}`]: {
              color: 'inherit',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(0.75, 1),
            },
          },
          colorInherit: {
            color: 'inherit',
            [`&.${buttonClasses.disabled}`]: {
              color: 'inherit',
              opacity: 0.26,
            },
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          colorDisabled: {
            color: 'inherit',
            opacity: 0.26,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorDisabled: {
            color: 'inherit',
            opacity: 0.26,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: theme.spacing(5),
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            '& a': {
              color: themeColors.teal[500],
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            height: theme.spacing(9),
            width: theme.spacing(9),
          },
          sizeSmall: {
            height: theme.spacing(5),
            width: theme.spacing(5),
          },
          sizeMedium: {
            height: theme.spacing(7),
            width: theme.spacing(7),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: theme.spacing(5),
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            lineHeight: 'normal',
            minHeight: theme.spacing(5),
            [theme.breakpoints.up('sm')]: {
              minWidth: theme.spacing(9),
            },
            '& .MuiTypography-overline': {
              letterSpacing: '0.02857em',
              lineHeight: 1.43,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: APP_NAVBAR_HEIGHT_PX,
          },
          regular: {
            minHeight: APP_NAVBAR_HEIGHT_PX,
            [theme.breakpoints.up('sm')]: {
              minHeight: APP_NAVBAR_HEIGHT_PX,
            },
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            color: themeColors.teal[500],
          },
        },
      },
    },
    palette: {
      DataGrid: {
        bg: themeColors.white[1000],
      },
    } as PaletteOptions,
  },
  svSE,
  datagridLocale
);
/** Bredd i pixlar per breakpoint */
export const breakpointsValues = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};
/**
 * OBS! Notera att om du vill lägga till overrides i AvenyTheme,
 * så måste du göra en spread på ...BaseTheme.overrides, precis som i
 * AvenyThemeDark. I annat fall skrivs BaseTheme.overrides över av
 * AvenyTheme.overrides
 * Det är en shallow merge som sker mellan stilarna, så krävs mer får man använda
 * Ramda > mergeDeepRight https://ramdajs.com/docs/#mergeDeepRight, som finns i stacken
 */
const AvenyTheme = createTheme(
  {
    ...BaseTheme,
    breakpoints: {
      values: breakpointsValues,
    },
    palette: {
      // Preserve BaseTheme palette (including palette.DataGrid)
      ...BaseTheme.palette,
      mode: 'light',
      primary: {
        light: themeColors.teal[400],
        main: themeColors.teal[500],
        dark: themeColors.teal[700],
      },
      secondary: {
        light: themeColors.blueGray[600],
        main: themeColors.blueGray[700],
        dark: themeColors.blueGray[800],
      },
      error: {
        light: themeColors.red.A300,
        main: themeColors.red.A500,
        dark: themeColors.red[900],
      },
      background: {
        paper: themeColors.white[1000],
        default: themeColors.gray[200],
      },
      text: {
        primary: themeColors.blackFade[1000.87],
        secondary: themeColors.blackFade[1000.6],
        disabled: themeColors.blackFade[1000.38],
      },
      action: {
        hover: themeColors.blueGrayFade[200.87],
      },
    },
    components: {
      ...BaseTheme.components,
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: BaseTheme.customProperties.MenuList.baseBg,
            boxShadow: theme.shadows[8],
          },
        },
      },
    },
  },
  svSE
);

export const AvenyThemeDark = createTheme(
  {
    ...BaseTheme,
    palette: {
      // Preserve BaseTheme palette (including palette.DataGrid)
      ...BaseTheme.palette,
      mode: 'dark',
      primary: {
        light: themeColors.teal[300],
        main: themeColors.teal[500],
        dark: themeColors.teal[700],
        contrastText: themeColors.white[1000],
      },
      secondary: {
        light: themeColors.blueGray[600],
        main: themeColors.blueGray[700],
        dark: themeColors.blueGray[800],
        contrastText: themeColors.white[1000],
      },
      error: {
        light: themeColors.red.A300,
        main: themeColors.red.A500,
        dark: themeColors.red[900],
      },
      background: {
        paper: themeColors.blueGray[700],
        default: themeColors.blueGray[800],
      },
      text: {
        primary: themeColors.whiteFade[1000.96],
        secondary: themeColors.whiteFade[1000.74],
        disabled: themeColors.whiteFade[1000.38],
      },
      action: {
        hover: themeColors.white[1000],
      },
    },
    components: {
      ...BaseTheme.components,
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            padding: theme.spacing(0, 0.5),
            [`&.${outlinedInputClasses.disabled}`]: {
              backgroundColor: themeColors.blackFade[1000.08],
              color: themeColors.whiteFade[1000.74],
            },
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderWidth: 2,
              borderColor: themeColors.whiteFade[1000.96],
            },
            [`&:hover.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline}`]:
              {
                borderWidth: 1,
              },
            [`&.${outlinedInputClasses.focused}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: themeColors.white[1000],
              },
            },
          },
          disabled: {},
          adornedStart: {},
          adornedEnd: {},
          error: {},
          sizeSmall: {},
          multiline: {},
          notchedOutline: {
            borderColor: themeColors.white[1000],
            transition: 'border-width 0s',
          },
          input: {
            fontSize: '0.875rem',
            padding: theme.spacing(1.25),
            height: '1.3575em',
            [theme.breakpoints.down('sm')]: {
              fontSize: '1rem',
            },
          },
          inputSizeSmall: {
            paddingTop: theme.spacing(1.25),
            paddingBottom: theme.spacing(1.25),
          },
          inputMultiline: {},
          inputAdornedStart: {},
          inputAdornedEnd: {},
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875em',
          },
          filled: {
            transform: 'translate(8px, 16px) scale(1)',
            [`&.${inputLabelClasses.shrink}`]: {
              transform: 'translate(4px, 4px) scale(0.75)',
            },
            [`&.${inputLabelClasses.sizeSmall}`]: {
              transform: 'translate(8px, 12px) scale(1)',
              '&$shrink': {
                transform: 'translate(4px, 4px) scale(0.75)',
              },
            },
          },
          outlined: {
            color: BaseTheme.customProperties.Fade.LightOnDark.mediumEmphasis,
            transform: 'translate(14px, 10px) scale(1)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 1.2rem)',
            paddingRight: '0.4rem',
            [`&.${inputLabelClasses.error}`]: {
              color: themeColors.red.A500,
            },
            [`&.${inputLabelClasses.shrink}`]: {
              transform: 'translate(14px, -7px) scale(0.75)',
              maxWidth: 'calc(100% - 1.6rem)',
              color: BaseTheme.customProperties.Fade.LightOnDark.highEmphasis,
              [`&.${inputLabelClasses.focused}`]: {
                color: BaseTheme.customProperties.Fade.LightOnDark.highEmphasis,
                [`&.${inputLabelClasses.error}`]: {
                  color: themeColors.red.A500,
                },
              },
              '& + div legend': {
                maxWidth: 'calc(100% - 0.8rem)',
              },
            },
            [`&.${inputLabelClasses.disabled}`]: {
              color: BaseTheme.customProperties.Fade.LightOnDark.lowEmphasis,
            },
          },
        },
      },
    },
  },
  svSE
);

/** Grundenheten för spacing. Använd normal theme.spacing(x). Den här konstanten kan användas om man behöver räkna på spacing utan att får med enhet */
export const AvenyThemeSpacing = 8;

//** Alternativ till theme.shape.borderradius. Om man behöver det som en siffra för att t.ex. beräkna ett relativt värde */
export const AvenyThemeShapeBorderRadius = 4;

export default AvenyTheme;
